/**
 * PATH: apps/qa/src/pages/records/JobQaHomeRow.tsx
 * FILE_ID: QA-RECORDS-JOB-ROW-001
 * PURPOSE: One job on the QA home list. Latest QA opens the hub. No QA seeds a blank list.
 */
import { Archive, Building2, CheckCircle, Clock, FileText, Loader2, Play, Send } from 'lucide-react'
import api from '@/lib/api'
import { jobQaHubPath, qaRecordPath } from '../records-list/openJobQaInHub'
import { cn, formatDate } from '@/lib/utils'
import { InspectionRecordLink } from '../inspectionRecordLink'
import type { QaJobRow } from './useJobRows'

function qaHttp() {
  const client = api as unknown as { post?: (url: string, body?: unknown) => Promise<unknown> } & (() => { post: (url: string, body?: unknown) => Promise<unknown> })
  if (typeof client.post === 'function') return client
  return client()
}

export async function openBlankQa(row: QaJobRow) {
  const created = await qaHttp().post('/qa/inspections/blank/', {
    job_id: row.job_id,
    only_if_empty: true,
    inspection_name: `${row.job_id}_QA`,
  }) as { data?: { id?: unknown; inspection?: { id?: unknown } | null } }
  const id = created?.data?.id ?? created?.data?.inspection?.id
  window.location.assign(id ? qaRecordPath(String(id)) : jobQaHubPath(String(row.job_id)))
}

function jobLine(row: QaJobRow): string {
  const parts = [`Job #${row.job_id}`]
  if (row.client_name) parts.push(row.client_name)
  if (row.site_address) parts.push(row.site_address)
  return parts.join(' · ')
}

// Reused from: apps/qa/src/pages/RecordsPage.tsx (the inspection list status icon and pill, before the job-row rewrite).
function statusIcon(status: string) {
  if (status === 'submitted') return <Send className="w-4 h-4" />
  if (status === 'in_progress') return <Play className="w-4 h-4" />
  if (status === 'ready_for_pack') return <CheckCircle className="w-4 h-4" />
  if (status === 'published') return <Clock className="w-4 h-4" />
  if (status === 'archived') return <Archive className="w-4 h-4" />
  return <FileText className="w-4 h-4" />
}

function statusStyles(status: string): { bg: string; text: string; icon: string } {
  if (status === 'published') return { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: 'text-purple-500' }
  if (status === 'in_progress') return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: 'text-blue-500' }
  if (status === 'ready_for_pack') return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: 'text-amber-500' }
  if (status === 'submitted') return { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: 'text-emerald-500' }
  return { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'text-muted-foreground' }
}

export function JobQaHomeRow({
  row,
  mobile,
  pending,
  onOpenEmpty,
}: {
  row: QaJobRow
  mobile: boolean
  pending: boolean
  onOpenEmpty: (row: QaJobRow) => void
}) {
  const latest = row.latest
  const status = String(latest?.status || 'draft')
  const styles = statusStyles(latest ? status : 'draft')
  const older = row.qa_count > 1 ? `+${row.qa_count - 1} older` : ''
  const body = (
    <>
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', styles.bg)}>
        <span className={styles.icon}>{statusIcon(latest ? status : 'draft')}</span>
      </div>
      <div className="min-w-0 flex-1">
        {latest ? (
          <p className="font-medium text-foreground truncate">
            {latest.inspection_name || 'Untitled Inspection'}
            {older ? <span className="ml-2 text-sm font-normal text-muted-foreground">{older}</span> : null}
          </p>
        ) : null}
        <div className="mt-0.5 flex min-w-0 items-center gap-3">
          <span className="flex min-w-0 items-center gap-1 truncate text-sm text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{jobLine(row)}</span>
          </span>
          {latest?.category ? (
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] capitalize text-muted-foreground">
              {latest.category}
            </span>
          ) : null}
        </div>
      </div>
      {latest ? (
        <div className="ml-4 shrink-0 text-right">
          <span className={cn('inline-block rounded-full px-2.5 py-1 text-xs font-medium', styles.bg, styles.text)}>
            {status.replace(/_/g, ' ')}
          </span>
          {latest.created_at ? (
            <p className="mt-1 text-xs text-muted-foreground">{formatDate(latest.created_at)}</p>
          ) : null}
        </div>
      ) : null}
    </>
  )
  if (!latest) {
    return (
      <button
        type="button"
        disabled={pending}
        onClick={() => onOpenEmpty(row)}
        className="flex min-h-[44px] w-full items-center gap-4 p-4 text-left hover:bg-accent/50 disabled:opacity-60"
      >
        {pending ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> : null}
        {body}
      </button>
    )
  }
  return (
    <InspectionRecordLink
      mobile={mobile}
      inspectionId={latest.id}
      jobId={row.job_id}
      companySlug={row.company_slug}
      className="flex min-h-[44px] items-center justify-between p-4 hover:bg-accent/50"
    >
      {body}
    </InspectionRecordLink>
  )
}
